import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { usePerm } from "@/context/AuthContext";
import { X, ChevronLeft, ChevronRight, Copy, RefreshCw, Eye, EyeOff } from "lucide-react";
import { StatusBadge } from "@/components/Status";

const PHONE_MODELS = {
  yealink: [
    { model: "T33G",  label: "T33G — 4 touches" },
    { model: "T42S",  label: "T42S — 3 touches" },
    { model: "T42U",  label: "T42U — 3 touches" },
    { model: "T43U",  label: "T43U — 3 touches" },
    { model: "T46S",  label: "T46S — 6 touches" },
    { model: "T46U",  label: "T46U — 6 touches" },
    { model: "T48S",  label: "T48S — 8 touches" },
    { model: "T48U",  label: "T48U — 8 touches" },
    { model: "T54W",  label: "T54W — 16 touches" },
    { model: "T57W",  label: "T57W — 27 touches" },
    { model: "T58A",  label: "T58A — 27 touches" },
    { model: "W52P",  label: "W52P (DECT)" },
    { model: "W56P",  label: "W56P (DECT)" },
    { model: "W73P",  label: "W73P (DECT)" },
  ],
  fanvil: [
    { model: "X4",   label: "X4 — 4 touches" },
    { model: "X4U",  label: "X4U — 4 touches" },
    { model: "X4G",  label: "X4G — 4 touches" },
    { model: "X5U",  label: "X5U — 8 touches" },
    { model: "X6",   label: "X6 — 6 touches" },
    { model: "X6U",  label: "X6U — 6 touches" },
    { model: "X7",   label: "X7 — 6 touches" },
    { model: "X7C",  label: "X7C — 6 touches" },
    { model: "V62",  label: "V62 — 2 touches" },
    { model: "V64",  label: "V64 — 4 touches" },
    { model: "V65",  label: "V65 — 6 touches" },
  ],
  cisco: [
    { model: "CP-3905", label: "CP-3905 — 3 touches" },
  ],
};

// OUI prefix → vendor. Source: IEEE OUI registry, restricted to hotel SIP phones.
const OUI_VENDOR = {
  "00:15:65": "yealink", "80:5E:C0": "yealink", "00:1D:E9": "yealink",
  "00:A8:59": "fanvil",  "08:A8:59": "fanvil",  "38:8B:59": "fanvil",  "48:7B:55": "fanvil",
  "00:1F:C1": "cisco",   "00:17:E0": "cisco",   "7C:95:F3": "cisco",   "88:43:E1": "cisco",
};

function detectVendorFromMac(mac) {
  if (!mac) return null;
  const normalized = mac.trim().replace(/-/g, ":").toUpperCase();
  const oui = normalized.slice(0, 8).toLowerCase();
  return OUI_VENDOR[oui] || null;
}

function SecretField({ value }) {
  const [show, setShow] = useState(false);
  const v = value ?? "";
  return (
    <div className="flex items-center gap-1.5">
      <span className="flex-1 rounded-sm bg-zinc-100 px-2 py-0.5 font-mono text-[11px]">
        {show ? v : "••••••••••••••••"}
      </span>
      <button type="button" onClick={() => setShow(!show)} className="text-zinc-400 hover:text-zinc-700">
        {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </button>
      <button
        type="button"
        onClick={() => { navigator.clipboard.writeText(v); toast.success("Copié"); }}
        className="text-zinc-400 hover:text-blue-600"
      >
        <Copy className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default function PhoneConfigDrawer({ hotelId, hotel, rooms, roomId, onClose, onRoomChange, onUpdate }) {
  const canManage = usePerm("can_manage_rooms");
  const [speedDials, setSpeedDials] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [provisioning, setProvisioning] = useState(false);

  const idx = rooms.findIndex((r) => r.id === roomId);
  const room = rooms[idx] || null;

  const [localVendor, setLocalVendor] = useState(room?.phone_vendor || "");
  const [localModel, setLocalModel] = useState(room?.phone_model || "");

  useEffect(() => {
    setLocalVendor(room?.phone_vendor || "");
    setLocalModel(room?.phone_model || "");
  }, [roomId, room?.phone_vendor, room?.phone_model]);

  const BACKEND = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const provisionUrl = room?.provision_token ? `${BACKEND}/api/p/${room.provision_token}` : null;
  const extKey = hotel?.external_line_key || { number: "9", label: "Ligne extérieure" };

  useEffect(() => {
    if (!hotelId) return;
    const ctrl = new AbortController();
    api.get(`/hotels/${hotelId}/speed-dials`, { signal: ctrl.signal })
      .then((r) => setSpeedDials(r.data))
      .catch((e) => { if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") return; });
    return () => ctrl.abort();
  }, [hotelId]);

  useEffect(() => {
    setCfg(null);
  }, [roomId]);

  const save = useCallback(async (patch) => {
    try {
      await api.put(`/rooms/${room?.id}`, patch);
      onUpdate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [room?.id, onUpdate]);

  const resetToken = useCallback(async () => {
    if (!window.confirm("Régénérer l'URL de provisioning ? L'ancienne URL sera immédiatement invalide.")) return;
    try {
      await api.post(`/rooms/${room?.id}/reset-provision-token`);
      toast.success("URL régénérée");
      onUpdate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [room?.id, onUpdate]);

  const provisionPhone = useCallback(async () => {
    setProvisioning(true);
    try {
      const { data } = await api.post(`/rooms/${room?.id}/provision-phone`);
      if (data.ok) toast.success(`Config poussée : ${data.filename}`);
      else toast.error(data.error || "Erreur inconnue");
      onUpdate();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setProvisioning(false);
    }
  }, [room?.id, onUpdate]);

  const viewConfig = useCallback(async () => {
    try {
      const { data } = await api.get(`/rooms/${room?.id}/provision`);
      setCfg(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [room?.id]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!room) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 z-50 flex w-[340px] flex-col bg-white shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between bg-zinc-900 px-4 py-3 text-white">
          <div>
            <div className="text-sm font-bold">Chambre {room.room_number}</div>
            <div className="text-xs text-zinc-400">{hotel?.name || hotelId}</div>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={room.status} />
            <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Nav chambres */}
        <div className="flex items-center justify-between border-b border-zinc-100 bg-zinc-50 px-4 py-1.5 text-[11px] text-zinc-500">
          <button
            type="button"
            disabled={idx === 0}
            onClick={() => onRoomChange(rooms[idx - 1].id)}
            className="flex items-center gap-0.5 disabled:opacity-30 hover:text-zinc-800"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> {idx > 0 ? rooms[idx - 1].room_number : "—"}
          </button>
          <span>{idx + 1} / {rooms.length}</span>
          <button
            type="button"
            disabled={idx === rooms.length - 1}
            onClick={() => onRoomChange(rooms[idx + 1].id)}
            className="flex items-center gap-0.5 disabled:opacity-30 hover:text-zinc-800"
          >
            {idx < rooms.length - 1 ? rooms[idx + 1].room_number : "—"} <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">

          {/* Identité SIP */}
          <section>
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Identité SIP</div>
            <div className="rounded-sm bg-zinc-50 p-3 space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="mb-0.5 text-[10px] text-zinc-400">Extension</div>
                  <div className="font-mono text-sm font-bold">{room.extension}</div>
                </div>
                <div>
                  <div className="mb-0.5 text-[10px] text-zinc-400">Serveur SIP</div>
                  <div className="text-xs text-blue-600">
                    {hotel?.sip_server_ip ? `${hotel.sip_server_ip}:${hotel.sip_server_port || 6060}` : "—"}
                  </div>
                </div>
              </div>
              {room.sip_username && room.sip_username !== room.extension && (
                <div>
                  <div className="mb-0.5 text-[10px] text-zinc-400">Auth SIP</div>
                  <div className="font-mono text-xs text-zinc-600">{room.sip_username}</div>
                </div>
              )}
              <div>
                <div className="mb-0.5 text-[10px] text-zinc-400">Secret SIP</div>
                <SecretField value={room.sip_password} />
              </div>
            </div>
          </section>

          {/* Téléphone */}
          <section>
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Téléphone</div>
            <div className="rounded-sm bg-zinc-50 p-3 space-y-2">
              <div className="flex gap-2">
                <select
                  value={localVendor}
                  onChange={(e) => {
                    setLocalVendor(e.target.value);
                    setLocalModel("");
                    save({ phone_vendor: e.target.value, phone_model: "" });
                  }}
                  disabled={!canManage}
                  className="flex-1 rounded-sm border border-zinc-200 px-2 py-1.5 text-xs focus:outline-none disabled:opacity-50"
                >
                  <option value="">— vendor —</option>
                  <option value="yealink">Yealink</option>
                  <option value="fanvil">Fanvil</option>
                  <option value="cisco">Cisco</option>
                </select>
                {localVendor && PHONE_MODELS[localVendor] && (
                  <select
                    value={localModel}
                    onChange={(e) => {
                      setLocalModel(e.target.value);
                      save({ phone_model: e.target.value });
                    }}
                    disabled={!canManage}
                    className="flex-1 rounded-sm border border-zinc-200 px-2 py-1.5 text-xs focus:outline-none disabled:opacity-50"
                  >
                    <option value="">— modèle —</option>
                    {PHONE_MODELS[localVendor].map((m) => (
                      <option key={m.model} value={m.model}>{m.label}</option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <div className="mb-0.5 text-[10px] text-zinc-400">Adresse MAC</div>
                <input
                  key={room.id}
                  defaultValue={room.mac || ""}
                  placeholder="AA:BB:CC:DD:EE:FF"
                  onBlur={(e) => {
                    const newMac = e.target.value;
                    if (newMac === room.mac) return;
                    const detected = !localVendor ? detectVendorFromMac(newMac) : null;
                    if (detected) {
                      toast.success(`Vendor détecté : ${detected}`);
                      setLocalVendor(detected);
                      setLocalModel("");
                      save({ mac: newMac, phone_vendor: detected, phone_model: "" });
                    } else {
                      save({ mac: newMac });
                    }
                  }}
                  disabled={!canManage}
                  className="w-full rounded-sm border border-zinc-200 px-2 py-1.5 font-mono text-xs focus:border-blue-400 focus:outline-none disabled:opacity-50"
                />
              </div>
            </div>
          </section>

          {/* URL Provisioning — masqué pour Cisco (TFTP uniquement) */}
          {localVendor !== "cisco" && (
            <section>
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Provisioning</div>
              <div className="rounded-sm border border-blue-100 bg-blue-50 p-3 space-y-2">
                {provisionUrl ? (
                  <>
                    <div className="break-all rounded-sm border border-blue-200 bg-white px-2 py-1.5 font-mono text-[10px] text-blue-700">
                      {provisionUrl}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(provisionUrl); toast.success("URL copiée"); }}
                        className="flex flex-1 items-center justify-center gap-1 rounded-sm border border-blue-200 bg-white px-3 py-1.5 text-xs text-blue-700 hover:bg-blue-100"
                      >
                        <Copy className="h-3 w-3" /> Copier
                      </button>
                      {canManage && (
                        <button
                          type="button"
                          onClick={resetToken}
                          className="flex items-center gap-1 rounded-sm border border-blue-200 bg-white px-3 py-1.5 text-xs text-blue-700 hover:bg-blue-100"
                        >
                          <RefreshCw className="h-3 w-3" /> Régénérer
                        </button>
                      )}
                    </div>
                    <div className="text-[9px] text-blue-400">
                      Saisir dans : Téléphone › Paramètres › Auto Provision › URL serveur
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-zinc-400">Token non disponible</div>
                )}
              </div>
            </section>
          )}
          {localVendor === "cisco" && (
            <section>
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Provisioning</div>
              <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-500 space-y-1">
                <div className="font-medium text-zinc-700">Provisioning TFTP</div>
                <div>Le CP-3905 récupère sa config via TFTP (DHCP option 150). Cliquer sur <span className="font-medium">Envoyer la config</span> pour pousser le fichier <span className="font-mono">SEP{room.mac ? room.mac.replace(/:/g,"").toUpperCase() : "<MAC>"}.cnf.xml</span> sur l'agent.</div>
              </div>
            </section>
          )}

          {/* Touches */}
          <section>
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-400">Touches programmables</div>
            <div className="rounded-sm bg-zinc-50 p-3 space-y-3">

              {/* Touche système */}
              <div>
                <div className="mb-1.5 flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-amber-700">
                  <span>🔒</span> Touche système
                </div>
                <div className="flex items-center gap-2 rounded-sm border border-amber-200 bg-amber-50 px-2.5 py-2">
                  <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-sm bg-amber-600 text-[11px] font-bold text-white">
                    {extKey.number}
                  </div>
                  <div>
                    <div className="text-xs font-medium">{extKey.label}</div>
                    <div className="text-[9px] text-amber-700">Injectée automatiquement en touche 1</div>
                  </div>
                </div>
              </div>

              {/* Touches hôtel */}
              <div>
                <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-zinc-400">
                  Touches hôtel ({speedDials.length})
                </div>
                {speedDials.length === 0 ? (
                  <div className="text-[11px] text-zinc-400">Aucune touche configurée</div>
                ) : (
                  speedDials.map((sd, i) => (
                    <div key={sd.id} className="flex items-center gap-2 border-b border-zinc-100 py-1.5 last:border-0">
                      <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-sm bg-zinc-200 text-[9px] font-semibold text-zinc-600">
                        {i + 2}
                      </div>
                      <span className="flex-1 text-xs">{sd.label}</span>
                      <span className="font-mono text-[10px] text-zinc-400">{sd.target}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>

          {/* Config preview */}
          {cfg && (
            <section>
              <div className="mb-2 flex items-center justify-between text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
                <span>Aperçu config</span>
                <button type="button" onClick={() => setCfg(null)} className="text-zinc-400 hover:text-zinc-700"><X className="h-3 w-3" /></button>
              </div>
              <pre className="max-h-48 overflow-auto rounded-sm bg-zinc-900 p-3 text-[10px] text-zinc-200">
                {cfg?.content ?? "Erreur : format inattendu"}
              </pre>
            </section>
          )}

        </div>

        {/* Footer */}
        <div className="flex gap-2 border-t border-zinc-200 p-3">
          {canManage && (
            <button
              type="button"
              onClick={provisionPhone}
              disabled={provisioning || !room.mac || !localVendor}
              className="flex flex-1 items-center justify-center rounded-sm bg-zinc-900 px-3 py-2 text-xs font-semibold text-white hover:bg-zinc-700 disabled:opacity-40"
            >
              {provisioning ? "En cours…" : "Envoyer la config"}
            </button>
          )}
          <button
            type="button"
            onClick={viewConfig}
            disabled={!room.mac || !localVendor}
            className="rounded-sm border border-zinc-200 px-3 py-2 text-xs text-zinc-600 hover:bg-zinc-50 disabled:opacity-40"
          >
            Aperçu fichier
          </button>
        </div>

      </div>
    </>
  );
}
